import React, { useState } from "react";
import { Radio, Button, Space, Divider, Tooltip, Dropdown } from "antd";
import { UnorderedListOutlined, CalendarOutlined, LineChartOutlined, SyncOutlined, ExportOutlined, DownOutlined, TableOutlined } from "@ant-design/icons";
import { useMonitor } from "../context/MonitorContext";
import { VIEW_TYPE, VIEW_TYPE_LABELS } from "../constants/viewConstants";
import { exportApi } from "../../../../api/exportApi";
import { handleExportDownload } from "../../../../utils/exportUtils";
import dayjs from "dayjs";
import PermissionGuard from "../../../../components/auth/PermissionGuard";
import { useAuth } from "../../../../hooks/useAuth";
import { useTimeZone } from "../../../../contexts/TimeZoneContext";
import ValidationWarningButton from "./ValidationWarningCard";

/**
 * 航班监控头部组件
 * 包含页面标题、视图切换按钮和刷新按钮
 */
const MonitorHeader = () => {
    const { activeView, setActiveView, fetchFlights, loading, filters, pagination, sorter, airportsMap } = useMonitor();
    const [exportLoading, setExportLoading] = useState(false);
    const { timeZone: timeZoneMode } = useTimeZone();
    const { user } = useAuth();
    // 销售导出是第三项导出，只对有航班客户归属读权限的人开放
    const canSalesExport = Boolean(user?.is_superuser || (user?.permissions || []).includes("sales:flight-attribution:read"));

    const handleViewChange = (e) => {
        setActiveView(e.target.value);
    };

    const handleRefresh = () => {
        fetchFlights();
    };

    // 处理导出当前筛选后的所有航班数据
    const handleExportAllFiltered = async (exportMode = "standard") => {
        try {
            setExportLoading(true);

            // 准备筛选参数，与fetchFlights中使用的类似
            const exportParams = {
                ...filters,
                ...(filters.status && Array.isArray(filters.status) && { status: filters.status.join(",") }),
                ...(filters.dateRange &&
                    filters.dateRange[0] && {
                        start_date: dayjs.utc(filters.dateRange[0]).format("YYYY-MM-DD"),
                        end_date: dayjs.utc(filters.dateRange[1]).format("YYYY-MM-DD"),
                    }),
                ...(filters.registrations && Array.isArray(filters.registrations) && { aircraft_reg: filters.registrations.join(",") }),
                ...(filters.searchText && { search_text: filters.searchText }),
                ...(sorter.field && { sort_by: sorter.field, sort_order: sorter.order === "ascend" ? "asc" : "desc" }),
            };

            // 删除不需要的键
            delete exportParams.dateRange;
            delete exportParams.registrations;
            delete exportParams.searchText;

            // 结算 / 销售导出：时刻按当前页面时区从库内 UTC 换算，不跟电脑时区走
            if (exportMode === "settlement" || exportMode === "sales") {
                exportParams.timezone_mode = timeZoneMode;
                if (exportMode === "settlement" && airportsMap && Object.keys(airportsMap).length > 0) {
                    const airportTimezones = {};
                    Object.entries(airportsMap).forEach(([code, airport]) => {
                        if (airport.timezone) {
                            airportTimezones[code] = airport.timezone;
                        }
                    });
                    exportParams.airport_timezones = JSON.stringify(airportTimezones);
                }
            }

            // 调用导出API，传递导出模式
            const response = await exportApi.exportFlightOperations(exportParams, exportMode);

            // 处理响应
            if (response.data && response.data.download_url) {
                // 直接下载
                handleExportDownload(response.data.download_url, response.data.file_name || "flight_operations.xlsx");
            } else if (response.data && response.data.task_id) {
                // 异步任务，需要轮询状态
                // 实际项目中这里应该显示一个进度条，并轮询任务状态
                alert("导出任务已开始，请稍后...");
                // TODO: 实现轮询逻辑
            }
        } catch (error) {
            console.error("导出航班数据失败:", error);
            alert(`导出失败: ${error.message || "未知错误"}`);
        } finally {
            setExportLoading(false);
        }
    };

    // 导出菜单项
    const exportMenuItems = [
        {
            key: "standard",
            label: "标准导出",
            onClick: () => handleExportAllFiltered("standard"),
        },
        {
            key: "settlement",
            label: "结算导出",
            onClick: () => handleExportAllFiltered("settlement"),
        },
        // 销售导出：标准列 + 客户 + 订单号，跟着当前筛选走；没有本功能读权限的人看不见
        ...(canSalesExport
            ? [
                  {
                      key: "sales",
                      label: "销售导出",
                      onClick: () => handleExportAllFiltered("sales"),
                  },
              ]
            : []),
    ];

    return (
        <div className="page-header-compact">
            <div className="page-title-compact">
                航班监控
                <span style={{ color: "#999", fontSize: "12px", marginLeft: "8px" }}>v1.5</span>
            </div>
            <div className="page-action-compact">
                {/* 控制区 */}
                <Space size="middle" wrap>
                    <Radio.Group value={activeView} onChange={handleViewChange} buttonStyle="solid" optionType="button">
                        <Tooltip title={VIEW_TYPE_LABELS[VIEW_TYPE.LIST]}>
                            <Radio.Button value={VIEW_TYPE.LIST}>
                                <UnorderedListOutlined /> 列表
                            </Radio.Button>
                        </Tooltip>
                        <Tooltip title={VIEW_TYPE_LABELS[VIEW_TYPE.COMPACT]}>
                            <Radio.Button value={VIEW_TYPE.COMPACT}>
                                <TableOutlined /> 紧凑
                            </Radio.Button>
                        </Tooltip>
                        <Tooltip title={VIEW_TYPE_LABELS[VIEW_TYPE.CALENDAR]}>
                            <Radio.Button value={VIEW_TYPE.CALENDAR}>
                                <CalendarOutlined /> 日历
                            </Radio.Button>
                        </Tooltip>
                        <Tooltip title={VIEW_TYPE_LABELS[VIEW_TYPE.TIMELINE]}>
                            <Radio.Button value={VIEW_TYPE.TIMELINE}>
                                <LineChartOutlined /> 时间轴
                            </Radio.Button>
                        </Tooltip>
                    </Radio.Group>

                    <Divider type="vertical" />

                    <ValidationWarningButton />

                    <Tooltip title="刷新数据">
                        <Button icon={<SyncOutlined spin={loading} />} onClick={handleRefresh} loading={loading}>
                            刷新
                        </Button>
                    </Tooltip>
                    <PermissionGuard permission={["flight_operation:read", "flight_operation:read_basic"]} requireAll={false}>
                        <Dropdown menu={{ items: exportMenuItems }} trigger={["click"]} placement="bottomRight">
                            <Button icon={<ExportOutlined />} loading={exportLoading}>
                                导出
                                <DownOutlined />
                            </Button>
                        </Dropdown>
                    </PermissionGuard>
                </Space>
            </div>
        </div>
    );
};

export default MonitorHeader;
